import { QuotesConfigSchema, QuotesDataSchema, type QuotesConfig, type Quote } from '@hud/core';
import { registerWidget, type WidgetProps } from '../registry.js';
import { formatAge, useSourceData } from '../hooks/useSourceData.js';
import './quotes.css';

/**
 * A price watchlist from a scheduled `quotes` source.
 *
 * Provider-agnostic by construction: the fetcher normalises CoinGecko and
 * Finnhub into the same shape, so switching provider is a source edit and this
 * component never learns either API.
 */
function Quotes({ config }: WidgetProps<QuotesConfig>) {
  const state = useSourceData(config.source, QuotesDataSchema, config.staleAfterMinutes);

  if (state.status === 'missing') {
    return (
      <div class="hud-quotes__note">
        Waiting for the first fetch
        <div class="hud-quotes__path">{state.path}</div>
      </div>
    );
  }

  if (state.status === 'invalid') {
    return (
      <div class="hud-quotes__note">
        Unreadable data
        <div class="hud-quotes__path">{state.error}</div>
      </div>
    );
  }

  const { data, ageMinutes, stale } = state;

  if (data.quotes.length === 0) {
    return <div class="hud-quotes__note">No symbols configured</div>;
  }

  return (
    <div class="hud-quotes">
      <ul class="hud-quotes__list">
        {data.quotes.map((quote) => (
          <li class="hud-quotes__row" key={quote.symbol}>
            <span class="hud-quotes__symbol">{quote.label}</span>
            <span class="hud-quotes__price">{formatPrice(quote)}</span>
            {config.showChange ? (
              <span class={`hud-quotes__change ${changeClass(quote.changePercent)}`}>
                {formatChange(quote.changePercent)}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      <div class={stale ? 'hud-quotes__age hud-quotes__age--stale' : 'hud-quotes__age'}>
        {formatAge(ageMinutes)}
      </div>
    </div>
  );
}

/**
 * Cheap assets need more decimals than expensive ones — a coin trading at
 * $0.00003 formatted to two places reads as $0.00 and looks broken.
 */
function formatPrice(quote: Quote): string {
  const { price, currency } = quote;
  const digits = price >= 100 ? 2 : price >= 1 ? 2 : price >= 0.01 ? 4 : 8;

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(price);
  } catch {
    // A currency code Intl doesn't know must not blank the row.
    return `${price.toFixed(digits)} ${currency.toUpperCase()}`;
  }
}

function formatChange(percent: number | null): string {
  if (percent === null) return '—';
  const sign = percent > 0 ? '+' : '';
  return `${sign}${percent.toFixed(2)}%`;
}

function changeClass(percent: number | null): string {
  if (percent === null || percent === 0) return 'hud-quotes__change--flat';
  return percent > 0 ? 'hud-quotes__change--up' : 'hud-quotes__change--down';
}

registerWidget({
  type: 'quotes',
  schema: QuotesConfigSchema,
  component: Quotes,
});
